import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import type { ButtonSize, ButtonVariant } from './Button'
import styles from './IconButton.module.css'

interface BaseProps {
  /* Accessible name; also used as the tooltip. */
  label: string
  icon: ReactNode
  variant?: ButtonVariant
  size?: ButtonSize
  className?: string
}

interface AsButton extends BaseProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className'> {
  href?: undefined
}

interface AsLink extends BaseProps {
  href: string
  external?: boolean
  target?: string
  rel?: string
}

export type IconButtonProps = AsButton | AsLink

function classes({ variant = 'secondary', size = 'md', className }: BaseProps) {
  return [styles.btn, styles[variant], styles[size], className].filter(Boolean).join(' ')
}

export function IconButton(props: IconButtonProps) {
  if (props.href !== undefined) {
    const { href, external, target, rel, label, icon, variant, size, className } = props
    const cls = classes({ label, icon, variant, size, className })
    return external || target ? (
      <a href={href} target={target} rel={rel} className={cls} aria-label={label} title={label}>{icon}</a>
    ) : (
      <Link href={href} className={cls} aria-label={label} title={label}>{icon}</Link>
    )
  }

  const { label, icon, variant, size, className, type = 'button', ...rest } = props
  return (
    <button type={type} className={classes({ label, icon, variant, size, className })} aria-label={label} title={label} {...rest}>
      {icon}
    </button>
  )
}
